import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Divider,
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { getUserNotifications, markNotificationAsRead } from '../services/notificationService';

const fmtVnd = (n) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(n || 0);

const BonusPopup = () => {
  const [open, setOpen] = useState(false);
  const [bonuses, setBonuses] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getUserNotifications('all', false);
        if (cancelled || !result.success) return;
        const list = Array.isArray(result.data) ? result.data : [];
        const bonusItems = list.filter(n => n?.metadata?.kind === 'bonus' && !n.isRead);
        if (bonusItems.length > 0) {
          setBonuses(bonusItems);
          setOpen(true);
        }
      } catch (e) {
        // silent — popup tùy chọn, không crash app
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleClose = async () => {
    setOpen(false);
    try {
      await Promise.all(bonuses.map(b => markNotificationAsRead(b._id)));
    } catch (e) {
      console.error('Error marking bonus notifications as read', e);
    }
  };

  if (!open || bonuses.length === 0) return null;

  const totalAmount = bonuses.reduce((s, b) => s + (b.metadata?.amount || 0), 0);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return; // Bắt buộc xác nhận
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #fff8e1 0%, #ffffff 60%)',
        }
      }}
    >
      <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: '50%',
          background: 'linear-gradient(135deg, #ffb74d 0%, #ef6c00 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mx: 'auto', mb: 2,
          boxShadow: '0 8px 20px rgba(239,108,0,0.3)'
        }}>
          <CardGiftcardIcon sx={{ color: '#fff', fontSize: 40 }} />
        </Box>
        <Typography variant="h5" fontWeight={800} sx={{ color: '#e65100', mb: 0.5 }}>
          🎉 Bạn đã được thưởng!
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {bonuses.length === 1 ? 'Bạn nhận được 1 khoản thưởng cuối kỳ.' : `Bạn nhận được ${bonuses.length} khoản thưởng cuối kỳ.`}
        </Typography>

        <Box sx={{
          py: 1.5, px: 2, borderRadius: 2, bgcolor: '#fff3e0',
          border: '1px dashed #ffb74d', mb: 2
        }}>
          <Typography variant="caption" color="text.secondary">Tổng thưởng</Typography>
          <Typography variant="h4" sx={{ color: '#e65100', fontWeight: 800 }}>
            {fmtVnd(totalAmount)}
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={1.5} sx={{ textAlign: 'left' }}>
          {bonuses.map((b) => (
            <Box key={b._id}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Kỳ {b.metadata?.periodStart ? new Date(b.metadata.periodStart).toLocaleDateString('vi-VN') : '-'}
                  {' — '}
                  {b.metadata?.periodEnd ? new Date(b.metadata.periodEnd).toLocaleDateString('vi-VN') : '-'}
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#e65100' }}>
                  {fmtVnd(b.metadata?.amount)}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Lý do: {b.metadata?.reason || b.message}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Khoản thưởng đã được cộng vào tổng thu nhập của kỳ tương ứng.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          fullWidth
          variant="contained"
          color="warning"
          size="large"
          onClick={handleClose}
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          Đã nhận, cảm ơn!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BonusPopup;
